import React, { useState } from 'react';
import { BUFF_TYPES, BUFF_MAP, INSPIRE_DICE } from '../../lib/buffs';
import { useCampaign } from '../../contexts/CampaignContext';
import { useMap } from '../../contexts/MapContext';
import { buildWallEdges, buildWallBlockedCells, computeReachable } from '../../lib/pathfinding';
import Tooltip from './Tooltip';

export default function BuffPanel({ token }) {
  const { updateToken, spawnToken, moveToken, tokens, removeToken } = useCampaign();
  const { cells, gridW, gridH, walls } = useMap();

  const [selectedBuff, setSelectedBuff] = useState('hasted');
  const [duration,     setDuration]     = useState(1);
  const [customLabel,  setCustomLabel]  = useState('');
  const [inspireDie,   setInspireDie]   = useState('d8');

  const allBuffs = token.buffs || [];
  // Hide mirror_image from display so DM can't identify the real token
  const buffs = allBuffs.filter((b) => b.id !== 'mirror_image' || !b.mirrorGroupId);

  // ── Add buff ──────────────────────────────────────────────────────────
  const handleAdd = async () => {
    const def = BUFF_MAP[selectedBuff];
    if (!def) return;

    const label = selectedBuff === 'custom' ? (customLabel.trim() || 'Custom')
      : selectedBuff === 'inspired' ? `Inspired (${inspireDie})`
      : def.label;

    // Don't add duplicate (same id)
    if (allBuffs.find((b) => b.id === selectedBuff && b.label === label)) return;

    const newBuff = {
      id:            selectedBuff,
      label,
      icon:          def.icon,
      color:         def.color,
      concentration: def.concentration,
      duration:      parseInt(duration, 10) || 1,
      permanent:     false,
    };
    if (selectedBuff === 'mirror_image') newBuff.charges = 3;

    // ── Mirror Image: spawn 3 duplicates + shuffle positions ──────────
    if (selectedBuff === 'mirror_image') {
      const groupId = `mi_${Date.now()}`;
      newBuff.mirrorGroupId = groupId;

      // Generate valid positions within 15ft (3 cells) — must be on terrain & reachable through walls
      const cx = token.x, cy = token.y;
      const RANGE = 3; // 15ft / 5ft per cell
      const tokenSize = token.size || 1;
      const wEdges = buildWallEdges(walls);
      const wBlocked = buildWallBlockedCells(walls);
      const { reachable } = computeReachable(cx, cy, RANGE, tokenSize, cells, null, gridW, gridH, wEdges, wBlocked);
      const occupied = new Set(Object.values(tokens).map((t) => `${t.x},${t.y}`));
      const candidates = [];
      for (const key of reachable) {
        if (occupied.has(key)) continue;
        const [nx, ny] = key.split(',').map(Number);
        candidates.push({ x: nx, y: ny });
      }
      // Shuffle and pick 4 positions
      for (let i = candidates.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
      }
      const positions = candidates.slice(0, 4);
      if (positions.length < 4) {
        // Not enough space — fill remaining with nearby positions
        while (positions.length < 4) positions.push({ x: cx + positions.length, y: cy });
      }

      // Assign real token to a random slot (0-3), duplicates fill the rest
      const realSlot = Math.floor(Math.random() * 4);
      const dupeIds = [];
      for (let i = 0; i < 4; i++) {
        if (i === realSlot) {
          // Move real token to this position
          await moveToken(token.id, positions[i].x, positions[i].y);
        } else {
          const dupeId = await spawnToken({
            name: token.name,
            type: token.type,
            colour: token.colour,
            size: token.size || 1,
            x: positions[i].x,
            y: positions[i].y,
            hp: token.hp,
            maxHp: token.maxHp,
            ac: token.ac,
            speed: token.speed,
            visionRange: 0,
            initiativeBonus: token.initiativeBonus,
            class: token.class,
            level: token.level,
          });
          await updateToken(dupeId, {
            mirrorDuplicate: true,
            mirrorSourceId: token.id,
            mirrorGroupId: groupId,
            ownerId: token.ownerId || null,
          });
          dupeIds.push(dupeId);
        }
      }
      await updateToken(token.id, {
        buffs: [...allBuffs, newBuff],
        mirrorGroupId: groupId,
      });
      if (selectedBuff === 'custom') setCustomLabel('');
      return;
    }

    updateToken(token.id, { buffs: [...allBuffs, newBuff] });
    if (selectedBuff === 'custom') setCustomLabel('');
  };

  // Map visible index back to allBuffs index
  const visibleToAll = (visibleIdx) => {
    const visBuff = buffs[visibleIdx];
    return allBuffs.indexOf(visBuff);
  };

  // ── Remove buff ───────────────────────────────────────────────────────
  const removeBuff = async (visIdx) => {
    const allIdx = visibleToAll(visIdx);
    const buff = allBuffs[allIdx];
    const next = allBuffs.filter((_, i) => i !== allIdx);

    // Clean up mirror image duplicates when buff is removed
    if (buff?.id === 'mirror_image' && buff.mirrorGroupId) {
      const dupes = Object.values(tokens).filter(
        (t) => t.mirrorDuplicate && t.mirrorGroupId === buff.mirrorGroupId
      );
      await Promise.all(dupes.map((t) => removeToken(t.id)));
      await updateToken(token.id, { buffs: next, mirrorGroupId: null });
      return;
    }

    updateToken(token.id, { buffs: next });
  };

  // ── Edit duration inline ──────────────────────────────────────────────
  const setBuffDuration = (visIdx, val) => {
    const allIdx = visibleToAll(visIdx);
    const next = allBuffs.map((b, i) => i === allIdx ? { ...b, duration: Math.max(0, parseInt(val, 10) || 0) } : b);
    updateToken(token.id, { buffs: next });
  };

  // ── Toggle permanent ──────────────────────────────────────────────────
  const togglePermanent = (visIdx) => {
    const allIdx = visibleToAll(visIdx);
    const next = allBuffs.map((b, i) => i === allIdx ? { ...b, permanent: !b.permanent } : b);
    updateToken(token.id, { buffs: next });
  };

  // ── Adjust charges (mirror image etc.) ────────────────────────────────
  const adjustCharges = async (visIdx, delta) => {
    const allIdx = visibleToAll(visIdx);
    const buff = allBuffs[allIdx];
    const newCharges = Math.max(0, (buff.charges || 0) + delta);

    if (newCharges === 0 && buff.id === 'mirror_image' && buff.mirrorGroupId) {
      // All charges gone — remove buff + all duplicates
      const dupes = Object.values(tokens).filter(
        (t) => t.mirrorDuplicate && t.mirrorGroupId === buff.mirrorGroupId
      );
      await Promise.all(dupes.map((t) => removeToken(t.id)));
      const next = allBuffs.filter((_, i) => i !== allIdx);
      await updateToken(token.id, { buffs: next, mirrorGroupId: null });
      return;
    }

    // Remove a random duplicate when decrementing
    if (delta < 0 && buff.id === 'mirror_image' && buff.mirrorGroupId) {
      const dupes = Object.values(tokens).filter(
        (t) => t.mirrorDuplicate && t.mirrorGroupId === buff.mirrorGroupId
      );
      if (dupes.length > 0) {
        const victim = dupes[Math.floor(Math.random() * dupes.length)];
        await removeToken(victim.id);
      }
    }

    const next = allBuffs.map((b, i) => {
      if (i !== allIdx) return b;
      return { ...b, charges: newCharges };
    });
    updateToken(token.id, { buffs: next });
  };

  const selectedDef = BUFF_MAP[selectedBuff];

  return (
    <div style={styles.panel}>
      {/* Active buffs */}
      {buffs.length > 0 ? (
        <div style={styles.buffList}>
          {buffs.map((buff, idx) => (
            <div key={idx} style={styles.buffRow}>
              <Tooltip text={BUFF_MAP[buff.id]?.desc}>
                <span style={{ ...styles.buffIcon, color: buff.color }}>{buff.icon}</span>
                <div style={styles.buffInfo}>
                  <span style={styles.buffLabel}>{buff.label}</span>
                  {buff.concentration && <span style={styles.concTag}>C</span>}
                </div>
              </Tooltip>
              {buff.permanent ? (
                <span style={styles.permTag} title="Permanent — click to make timed" onClick={() => togglePermanent(idx)}>∞</span>
              ) : (
                <div style={styles.durationEdit}>
                  <input
                    style={styles.durInput}
                    type="number"
                    min="0"
                    value={buff.duration}
                    onChange={(e) => setBuffDuration(idx, e.target.value)}
                    title="Rounds remaining"
                  />
                  <span style={styles.durLabel}>r</span>
                  <span style={styles.permHint} onClick={() => togglePermanent(idx)} title="Make permanent">∞</span>
                </div>
              )}
              {buff.charges !== undefined && (
                <div style={styles.chargesRow}>
                  <button style={styles.chargeBtn} onClick={() => adjustCharges(idx, -1)}>−</button>
                  <span style={styles.chargeCount}>×{buff.charges}</span>
                  <button style={styles.chargeBtn} onClick={() => adjustCharges(idx, 1)}>+</button>
                </div>
              )}
              <button style={styles.removeBtn} onClick={() => removeBuff(idx)} title="Remove buff">✕</button>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.empty}>No active buffs.</div>
      )}

      {/* Add buff */}
      <div style={styles.addSection}>
        <div style={styles.addLabel}>Add Buff</div>
        <select
          style={styles.select}
          value={selectedBuff}
          onChange={(e) => setSelectedBuff(e.target.value)}
        >
          {BUFF_TYPES.map((b) => (
            <option key={b.id} value={b.id}>{b.icon} {b.label}{b.concentration ? ' (C)' : ''}</option>
          ))}
        </select>

        {selectedBuff === 'custom' && (
          <input
            placeholder="Custom buff name…"
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            style={{ marginTop: 6 }}
          />
        )}

        {selectedBuff === 'inspired' && (
          <div style={styles.diceRow}>
            {INSPIRE_DICE.map((d) => (
              <button
                key={d}
                style={{ ...styles.dieBtn, ...(inspireDie === d ? styles.dieBtnActive : {}) }}
                onClick={() => setInspireDie(d)}
              >
                {d}
              </button>
            ))}
          </div>
        )}

        <div style={styles.durationRow}>
          <span style={styles.durRowLabel}>Duration (rounds)</span>
          <input
            style={styles.durInput}
            type="number"
            min="1"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
        </div>

        {selectedDef?.desc && (
          <div style={styles.descNote}>{selectedDef.desc}</div>
        )}
        {selectedDef?.concentration && (
          <div style={styles.concNote}>⚠ Concentration — strips when concentration drops</div>
        )}

        <button
          className="btn-primary w-full"
          style={{ marginTop: 8 }}
          onClick={handleAdd}
          disabled={selectedBuff === 'custom' && !customLabel.trim()}
        >
          + Add Buff
        </button>
      </div>
    </div>
  );
}

const styles = {
  panel:       { display: 'flex', flexDirection: 'column', gap: 0 },
  buffList:    { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 10 },
  buffRow:     { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', borderBottom: '1px solid #4a3d28' },
  buffIcon:    { fontSize: 15, flexShrink: 0 },
  buffInfo:    { flex: 1, display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' },
  buffLabel:   { fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  concTag:     { fontSize: 9, background: '#7c3aed', color: '#fff', padding: '1px 4px', borderRadius: 3, fontWeight: 800, flexShrink: 0 },
  durationEdit:{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 },
  durInput:    { width: 38, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 5, color: '#f0e6d0', padding: '3px 4px', fontSize: 12, outline: 'none', textAlign: 'center' },
  durLabel:    { fontSize: 10, color: '#9a8b6e' },
  permTag:     { fontSize: 14, color: '#d4a847', cursor: 'pointer', padding: '0 4px' },
  permHint:    { fontSize: 12, color: '#4a3d28', cursor: 'pointer', padding: '0 2px' },
  removeBtn:   { background: 'transparent', border: 'none', color: '#6a5d45', cursor: 'pointer', fontSize: 12, padding: '0 2px', borderRadius: 3, flexShrink: 0 },
  empty:       { color: '#6a5d45', fontSize: 12, padding: '4px 0 8px', textAlign: 'center' },
  addSection:  { borderTop: '1px solid #4a3d28', paddingTop: 10 },
  addLabel:    { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9a8b6e', marginBottom: 8 },
  select:      { width: '100%', background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '8px 10px', fontSize: 13, outline: 'none', cursor: 'pointer' },
  durationRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 },
  durRowLabel: { fontSize: 12, color: '#9a8b6e' },
  descNote:    { fontSize: 11, color: '#b0b0c8', background: 'rgba(100,100,140,0.15)', padding: '5px 8px', borderRadius: 6, marginTop: 6, lineHeight: 1.4 },
  concNote:    { fontSize: 11, color: '#d4a847', background: 'rgba(167,139,250,0.1)', padding: '5px 8px', borderRadius: 6, marginTop: 6 },
  diceRow:     { display: 'flex', gap: 6, marginTop: 6 },
  dieBtn:      { flex: 1, padding: '5px 0', background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 6, color: '#9a8b6e', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  dieBtnActive:{ background: '#3a2060', borderColor: '#d4a847', color: '#d4a847' },
  chargesRow:  { display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 },
  chargeBtn:   { background: 'transparent', border: '1px solid #4a3d28', borderRadius: 3, color: '#9a8b6e', cursor: 'pointer', fontSize: 12, fontWeight: 700, width: 18, height: 18, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 },
  chargeCount: { fontSize: 11, fontWeight: 700, color: '#818cf8', minWidth: 18, textAlign: 'center' },
};
